import { create } from 'zustand';
import type { NotificationStore, NotificationStoreState, Notification } from '@mellify/shared';

/**
 * Initial notification state.
 */
const initialState: NotificationStoreState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
};

/**
 * Calculate unread count from the notification list.
 */
function calculateUnreadCount(notifications: Notification[]): number {
  return notifications.filter((n) => !n.read).length;
}

/**
 * Notification Store
 *
 * Manages notification state including the notification list and unread count.
 * Notifications are not persisted to localStorage since they are fetched from
 * the API and may include real-time updates via WebSocket.
 *
 * @example
 * ```ts
 * const { notifications, unreadCount, markAsRead } = useNotificationStore();
 *
 * // With selector
 * const unreadCount = useNotificationStore((state) => state.unreadCount);
 * ```
 */
export const useNotificationStore = create<NotificationStore>()((set) => ({
  ...initialState,

  setNotifications: (notifications: Notification[]) => {
    set({
      notifications,
      unreadCount: calculateUnreadCount(notifications),
      error: null,
    });
  },

  addNotification: (notification: Notification) => {
    set((state) => {
      const notifications = [notification, ...state.notifications];
      return {
        notifications,
        unreadCount: calculateUnreadCount(notifications),
      };
    });
  },

  removeNotification: (notificationId: string) => {
    set((state) => {
      const notifications = state.notifications.filter((n) => n.id !== notificationId);
      return {
        notifications,
        unreadCount: calculateUnreadCount(notifications),
      };
    });
  },

  markAsRead: (notificationId: string) => {
    set((state) => {
      const notifications = state.notifications.map((n) =>
        n.id === notificationId
          ? { ...n, read: true, readAt: new Date().toISOString() }
          : n
      );
      return {
        notifications,
        unreadCount: calculateUnreadCount(notifications),
      };
    });
  },

  markAllAsRead: () => {
    const now = new Date().toISOString();
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.read ? n : { ...n, read: true, readAt: now }
      ),
      unreadCount: 0,
    }));
  },

  clearAll: () => {
    set({
      notifications: [],
      unreadCount: 0,
    });
  },

  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  setError: (error: string | null) => {
    set({ error });
  },

  recalculateUnreadCount: () => {
    set((state) => ({
      unreadCount: calculateUnreadCount(state.notifications),
    }));
  },
}));